import {Link} from 'react-router';
import dayjs from 'dayjs';
import {useDeleteProblemFlagMutation} from '@api';
import {ProblemFlag} from '@types';
import {isEmpty} from 'lodash';
import React, {useEffect, useState} from 'react';
import {Modal, Button, Form} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

const flagTranslationKeys: Record<string, string> = {
    'Well constructed': 'flags.well_constructed',
    'Needs fixing': 'flags.needs_fixing',
    Invalid: 'flags.invalid',
    Duplicate: 'flags.duplicate',
};

type ProblemFlagTableEntry = ProblemFlag & {
    action: 'add' | 'clear';
    timestamp: string;
};

type DeleteFlagModalProps = {
    show: boolean;
    onHide: () => void;
    onDelete: () => void;
    reason: string;
    setReason: (reason: string) => void;
};

const DeleteFlagModal: React.FC<DeleteFlagModalProps> = ({
    show,
    onHide,
    onDelete,
    reason,
    setReason,
}) => {
    const {t} = useTranslation();
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('problem_draft.delete_flag')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group controlId="delete-reason">
                    <Form.Label>{t('problem_draft.reason')}</Form.Label>
                    <Form.Control
                        type="text"
                        size="sm"
                        placeholder={t('problem_draft.enter_reason')}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="danger" onClick={onDelete}>
                    {t('common.delete')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const ProblemFlagsTable = ({
    flags,
    problemOwner,
    onDelete,
    showActionText = false,
    showActions = true,
    showRating = true,
}: {
    flags: ProblemFlag[];
    onDelete?: (flagId: number) => void;
    problemOwner?: boolean;
    showActions?: boolean;
    showActionText?: boolean;
    showRating?: boolean;
}) => {
    const [deleteProblemFlag] = useDeleteProblemFlagMutation();
    const [showModal, setShowModal] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [currentFlag, setCurrentFlag] = useState<ProblemFlag | null>(null);
    const [flattenedFlags, setFlattenedFlags] = useState<
        ProblemFlagTableEntry[]
    >([]);
    const {t} = useTranslation();

    const handleFlagDelete = async (
        flag: ProblemFlag,
        comment: string | null = null
    ) => {
        try {
            await deleteProblemFlag({
                id: flag.id,
                body: {comment},
            }).unwrap();
            toast.success('Flag deleted', {
                autoClose: 1000,
            });
            if (onDelete) onDelete(flag.id);
            setShowModal(false);
        } catch (error: any) {
            toast.error(error.data.errors[0].message);
        }
    };

    const deleteButton = (flag: ProblemFlag) => {
        return (
            <Button
                href="#"
                className="btn btn-danger btn-sm"
                onClick={async () => {
                    if (problemOwner) {
                        setCurrentFlag(flag);
                        setShowModal(true);
                    } else {
                        await handleFlagDelete(flag, 'Deleted by flag author');
                    }
                }}>
                {t('common.delete')}
            </Button>
        );
    };

    useEffect(() => {
        const entries: ProblemFlagTableEntry[] = flags.flatMap(flag => {
            const result: ProblemFlagTableEntry[] = [
                {
                    ...flag,
                    action: 'add' as const,
                    timestamp: flag.createdAt,
                },
            ];
            if (flag.deletedAt) {
                result.push({
                    ...flag,
                    action: 'clear' as const,
                    timestamp: flag.deletedAt,
                });
            }
            return result;
        });

        const sorted = entries.sort(
            (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
        );

        setFlattenedFlags(sorted);
    }, [flags]);

    return (
        <>
            <DeleteFlagModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onDelete={async () => {
                    if (!currentFlag) return;
                    await handleFlagDelete(currentFlag, deleteReason || null);
                }}
                reason={deleteReason}
                setReason={setDeleteReason}
            />
            <table className="table table-striped table-responsive table-sm">
                <thead>
                    <tr>
                        <th>{t('table_header.general.user')}</th>
                        {showRating && (
                            <th>{t('table_header.general.rating')}</th>
                        )}
                        <th>{t('table_header.general.when')}</th>
                        <th>{t('table_header.flag.flag')}</th>
                        <th>{t('table_header.general.description')}</th>
                        {(showActionText || showActions) && (
                            <th>{t('table_header.general.action')}</th>
                        )}
                        {showActionText && (
                            <>
                                <th>{t('table_header.general.cleared_by')}</th>
                                <th>{t('common.reason')}</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {flattenedFlags.map(flag => (
                        <tr
                            key={`${flag.id}-${flag.action}`}
                            data-id={flag.id}
                            data-own={flag.own}
                            className="align-middle">
                            <td>
                                <Link
                                    to={`/users/${flag.user}`}
                                    className="text-muted text-decoration-none">
                                    {flag.user}
                                </Link>
                            </td>
                            {showRating && <td>{flag.userRating}</td>}
                            <td>
                                {dayjs(flag.timestamp).format('YYYY-MM-DD')}
                            </td>
                            <td>
                                <span
                                    className={`badge bg-${flag.type === 'Well constructed' ? 'success' : 'danger'}`}>
                                    {flagTranslationKeys[flag.type]
                                        ? t(flagTranslationKeys[flag.type])
                                        : flag.type}
                                </span>
                            </td>
                            <td>
                                {isEmpty(flag.description)
                                    ? '-'
                                    : flag.description}
                            </td>
                            {problemOwner && showActions && (
                                <td>
                                    {flag.type === 'Well constructed'
                                        ? '-'
                                        : deleteButton(flag)}
                                </td>
                            )}
                            {!problemOwner && showActions && (
                                <td>{flag.own ? deleteButton(flag) : '-'}</td>
                            )}
                            {showActionText && (
                                <td>
                                    <span
                                        className={`badge bg-${flag.action === 'add' ? 'success' : 'danger'}`}>
                                        {t(`common.${flag.action}`)}
                                    </span>
                                </td>
                            )}
                            {showActionText && (
                                <>
                                    <td>
                                        {flag.action === 'clear' &&
                                        flag.clearedBy ? (
                                            <Link
                                                to={`/users/${flag.clearedBy}`}
                                                className="text-muted text-decoration-none">
                                                {flag.clearedBy}
                                            </Link>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>
                                        {flag.action === 'clear' &&
                                        !isEmpty(flag.clearReason)
                                            ? flag.clearReason
                                            : '-'}
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};
